import { PAGE_SIZE_OPTIONS } from '@/constants';
import ChevronDownIcon from '@/resources/images/icons-chevron-down.svg';
import SearchIcon from '@/resources/images/icons-search.svg';
import { KLAYO_COLORS } from '@/themes';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Paper,
  Stack,
  styled,
  TablePagination,
  Typography,
} from '@mui/material';
import { GridPaginationModel } from '@mui/x-data-grid-pro';
import { useState } from 'react';
import { TablePlaceholder } from './TablePlaceholder';
import { CourseStudentRow } from './table/CourseStudentsTable';

// The five status counters partition the enrolments; `overdue` CROSS-CUTS them (a past-due failure is
// counted in both), so it sits last rather than among them — the same order as the desktop columns.
const STATS: { labelKey: string; field: keyof CourseStudentRow; red?: boolean }[] = [
  { labelKey: 'tableColumns.enrolled', field: 'enrolledCount' },
  { labelKey: 'tableColumns.completed', field: 'completedCount' },
  { labelKey: 'tableColumns.inProgress', field: 'inProgressCount' },
  { labelKey: 'tableColumns.notStarted', field: 'notStartedCount' },
  { labelKey: 'tableColumns.failed', field: 'failedCount' },
  { labelKey: 'tableColumns.pending', field: 'pendingCount' },
  { labelKey: 'tableColumns.overdue', field: 'overdueCount', red: true },
];

interface StudentAccordionsProps {
  rows: CourseStudentRow[];
  totalCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  translate: (key: string, params?: any) => string;
  isLoading?: boolean;
}

export const StudentAccordions = ({
  rows,
  totalCount,
  paginationModel,
  onPaginationModelChange,
  translate,
  isLoading,
}: StudentAccordionsProps) => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (id: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? id : false);
  };

  return (
    <Paper variant='outlined' sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {isLoading ? (
        <Box display='flex' justifyContent='center' py={6}>
          <CircularProgress size={32} />
        </Box>
      ) : rows.length === 0 ? (
        <Box py={4}>
          <TablePlaceholder text={translate('manage.noStudentsFound')} icon={<SearchIcon />} />
        </Box>
      ) : (
        rows.map((row) => {
          const isExpanded = expanded === row.id;
          const enrolled = row.enrolledCount ?? 0;

          return (
            <Accordion
              key={row.id}
              expanded={isExpanded}
              onChange={handleChange(row.id)}
              disableGutters
              elevation={0}
              sx={{
                borderBottom: `1px solid ${KLAYO_COLORS.GreyGR100}`,
                '&:last-of-type': { borderBottom: 'none' },
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary
                expandIcon={<StyledChevronDownIcon />}
                sx={{
                  p: 2,
                  minHeight: 'unset',
                  '& .MuiAccordionSummary-content': { my: 0 },
                  '& .MuiTypography-root': {
                    fontSize: 'inherit',
                  },
                }}
              >
                <Stack spacing={0.5}>
                  <Typography variant='body1' fontWeight={600} fontSize='1rem !important'>
                    {row.fullName ?? row.name}
                  </Typography>
                  {!isExpanded && (
                    <Typography
                      variant='body2'
                      sx={{ color: KLAYO_COLORS.GreyGR700 }}
                      fontSize='0.875rem !important'
                      fontWeight={'400 !important'}
                    >
                      {translate('tableColumns.enrolled')}: {enrolled}
                    </Typography>
                  )}
                </Stack>
              </AccordionSummary>

              <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>
                <Stack gap={0.5}>
                  {STATS.map(({ labelKey, field, red }) => {
                    const value = (row[field] as number) ?? 0;
                    return (
                      <Stack key={labelKey} direction='row' gap={0.5}>
                        <Typography variant='body2' sx={{ color: KLAYO_COLORS.GreyGR900 }}>
                          {translate(labelKey)}:
                        </Typography>
                        <Typography
                          variant='body2'
                          sx={{ color: red ? KLAYO_COLORS.TouchRedTR500 : KLAYO_COLORS.GreyGR900 }}
                        >
                          {value}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })
      )}

      <TablePagination
        component='div'
        count={totalCount}
        page={paginationModel.page}
        rowsPerPage={paginationModel.pageSize}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        onPageChange={(_e, newPage) => {
          if (isLoading) return;
          onPaginationModelChange({ ...paginationModel, page: newPage });
        }}
        onRowsPerPageChange={(e) => {
          onPaginationModelChange({ page: 0, pageSize: Number(e.target.value) });
        }}
        labelRowsPerPage={translate('pagination.rowsPerPage')}
        labelDisplayedRows={({ from, to, count }) =>
          translate('pagination.displayedRows', {
            from,
            to,
            count: count !== -1 ? count : `>${to}`,
          })
        }
      />
    </Paper>
  );
};

const StyledChevronDownIcon = styled(ChevronDownIcon)(() => ({
  width: 20,
  height: 20,

  '& path[fill="#9CA3AF"]': {
    fill: KLAYO_COLORS.NeonBlueNB500,
  },
}));
